import { applyRate } from '@zuruflex/utils';
/**
 * What a promo takes off a rental of `base`.
 *
 * Never more than `base`: a ₵100 code on a ₵60 hire makes the hire free, it
 * does not pay the renter ₵40. Never negative, whatever a form let through.
 */
export function promoDiscountAmount(promo, base) {
    let amount;
    if (promo.discountType === 'PERCENT') {
        amount = applyRate(base, (promo.percentBps ?? 0) / 10_000);
        if (promo.maxDiscount != null)
            amount = Math.min(amount, promo.maxDiscount);
    }
    else {
        amount = promo.amountOff ?? 0;
    }
    return Math.max(0, Math.min(amount, base));
}
/**
 * Whether `promo` may be applied to this booking, and if not, the first
 * reason why.
 *
 * The rejection vocabulary is what the renter is shown, so a few states
 * share a reason rather than inventing one nobody could act on: a paused or
 * archived promo reads as `expired`, and a promo in another currency or
 * country — or on somebody else's fleet — reads as `notEligibleVehicle`.
 *
 * Checks run cheapest and least personal first, so an anonymous quote and a
 * signed-in one disagree only about the customer's own history.
 */
export function checkEligibility(promo, ctx) {
    const reject = (reason) => ({ ok: false, reason });
    const now = ctx.now.getTime();
    if (promo.status !== 'ACTIVE')
        return reject('expired');
    if (new Date(promo.startsAt).getTime() > now)
        return reject('notStarted');
    // `endsAt` is exclusive.
    if (promo.endsAt != null && new Date(promo.endsAt).getTime() <= now)
        return reject('expired');
    if (promo.maxRedemptions != null && promo.redemptionCount >= promo.maxRedemptions) {
        return reject('exhausted');
    }
    if (promo.currency.toUpperCase() !== ctx.currency.toUpperCase()) {
        return reject('notEligibleVehicle');
    }
    if (promo.countryCode &&
        ctx.countryCode &&
        promo.countryCode.toUpperCase() !== ctx.countryCode.toUpperCase()) {
        return reject('notEligibleVehicle');
    }
    // A partner's promo is confined to their own fleet whatever its lists say.
    if (promo.funder === 'PARTNER' && promo.partnerId !== ctx.partnerId) {
        return reject('notEligibleVehicle');
    }
    if (!coversAll(promo.partnerIds, ctx.partnerId))
        return reject('notEligibleVehicle');
    if (!coversAll(promo.vehicleIds, ctx.vehicleId))
        return reject('notEligibleVehicle');
    if (!coversAll(promo.locationIds, ctx.locationId))
        return reject('notEligibleVehicle');
    if (promo.minDays != null && ctx.days < promo.minDays)
        return reject('minDays');
    if (promo.minSubtotal != null && ctx.base < promo.minSubtotal)
        return reject('minSubtotal');
    const customer = ctx.customer;
    if (customer) {
        if (promo.audience === 'FIRST_HIRE' && customer.priorHires > 0)
            return reject('firstHireOnly');
        if (promo.audience === 'ASSIGNED' && !customer.isAssigned)
            return reject('notAssigned');
        if (customer.usesOfThisPromo >= promo.maxPerCustomer)
            return reject('perCustomerLimit');
    }
    return { ok: true };
}
/** An empty list means "all", which is the whole reason it is allowed to be empty. */
function coversAll(ids, id) {
    return ids.length === 0 || ids.includes(id);
}
/**
 * Who keeps what of the subtotal, once a promo has had its say.
 *
 * Whoever created the promo pays for it:
 *
 *   • With no promo, commission is a share of what the renter paid. The
 *     long-hire discount is already inside `subtotal`, so both sides bear it
 *     pro rata, as they always have.
 *   • A Zuruflex promo leaves the partner exactly what they would have earned on
 *     the full price. Zuruflex's commission takes the whole discount, and on a
 *     generous enough promo goes below zero — that is Zuruflex paying the
 *     partner to have run the promo, which is the deal.
 *   • A partner promo leaves Zuruflex's commission on the full price, and the
 *     partner's earnings take the whole discount.
 *
 * Whichever branch runs, the two halves add up to `subtotal` exactly, so no
 * rounding ever creates or loses a pesewa between them.
 */
export function splitRevenue(input) {
    const promoDiscount = Math.max(0, input.promoDiscount ?? 0);
    const share = (amount) => Math.round((amount * input.commissionBps) / 10_000);
    if (promoDiscount > 0 && input.promoFunder) {
        const gross = input.subtotal + promoDiscount;
        if (input.promoFunder === 'ZURUFLEX') {
            const partnerEarnings = gross - share(gross);
            return { commissionAmount: input.subtotal - partnerEarnings, partnerEarnings };
        }
        const commissionAmount = share(gross);
        return { commissionAmount, partnerEarnings: input.subtotal - commissionAmount };
    }
    const commissionAmount = share(input.subtotal);
    return { commissionAmount, partnerEarnings: input.subtotal - commissionAmount };
}
